import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { googleLogout } from "@react-oauth/google";
import SearchBar from "./ReactSearchComponent";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Dropdown from "react-bootstrap/Dropdown";
import { encodeId } from "../HelperMethods";
import "./Navbar.css";
import { getCacheBustedImageUrl, handleImageError, refreshImageCache } from "../../utils/imageUtils";

// User type definition
type User = {
  given_name: string;
  dubu_user: string;
  [key: string]: any;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const userRef = useRef<User | null>(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1042);

  const groupDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const soloDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  // Update ref when user state changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    // Handle window resize to detect mobile view
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1042);
    };
    window.addEventListener('resize', handleResize);

    // Load user from localStorage on component mount
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Listen for storage events (login/logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user") {
        if (e.newValue) {
          try {
            const userData = JSON.parse(e.newValue);
            setUser(userData);
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        } else {
          setUser(null);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Listen for login state changes
    const handleLoginStateChange = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        setUser(null);
      }
    };
    window.addEventListener("loginStateChange", handleLoginStateChange);

    // Listen for photo updates
    const handlePhotoUpdate = (e: CustomEvent) => {
      if (userRef.current) {
        const updatedUser = { ...userRef.current, picture: e.detail.picture };
        setUser(updatedUser);

        // Refresh the cache for the new image URL
        if (e.detail.picture) {
          refreshImageCache(e.detail.picture);
        }
      }
    };
    window.addEventListener('photoUpdate', handlePhotoUpdate as EventListener);

    // Add click outside handler to close dropdowns
    const handleClickOutside = (event: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        closeDropdowns();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("loginStateChange", handleLoginStateChange);
      window.removeEventListener('photoUpdate', handlePhotoUpdate as EventListener);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener("mousedown", handleClickOutside);

      // Clear any remaining timeouts
      if (groupDropdownTimeoutRef.current) {
        clearTimeout(groupDropdownTimeoutRef.current);
      }
      if (soloDropdownTimeoutRef.current) {
        clearTimeout(soloDropdownTimeoutRef.current);
      }
    };
  }, []); // Remove user from dependency array

  useEffect(() => {
    setShowGroupDropdown(false);
    setShowSoloDropdown(false);
    setIsOpen(false); // Also close the mobile menu if open
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Close any open submenus when toggling the main menu
    setShowGroupDropdown(false);
    setShowSoloDropdown(false);
  };
  const closeMenu = () => {
    setIsOpen(false);
    setShowGroupDropdown(false);
    setShowSoloDropdown(false);
  };

  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showSoloDropdown, setShowSoloDropdown] = useState(false);

  const handleGroupMouseEnter = () => {
    if (!isMobile) {
      if (groupDropdownTimeoutRef.current) {
        clearTimeout(groupDropdownTimeoutRef.current);
        groupDropdownTimeoutRef.current = null;
      }
      setShowGroupDropdown(true);
    }
  };

  const handleGroupMouseLeave = () => {
    if (!isMobile) {
      groupDropdownTimeoutRef.current = setTimeout(() => {
        setShowGroupDropdown(false);
      }, 300);
    }
  };

  const handleSoloMouseEnter = () => {
    if (!isMobile) {
      if (soloDropdownTimeoutRef.current) {
        clearTimeout(soloDropdownTimeoutRef.current);
        soloDropdownTimeoutRef.current = null;
      }
      setShowSoloDropdown(true);
    }
  };

  const handleSoloMouseLeave = () => {
    if (!isMobile) {
      soloDropdownTimeoutRef.current = setTimeout(() => {
        setShowSoloDropdown(false);
      }, 300);
    }
  };

  const handleMobileDropdownToggle = (type: 'group' | 'solo') => {
    if (isMobile) {
      if (type === 'group') {
        setShowGroupDropdown(!showGroupDropdown);
        setShowSoloDropdown(false);
      } else {
        setShowSoloDropdown(!showSoloDropdown);
        setShowGroupDropdown(false);
      }
    }
  };

  // New function to close dropdowns only
  const closeDropdowns = () => {
    if (groupDropdownTimeoutRef.current) {
      clearTimeout(groupDropdownTimeoutRef.current);
    }
    if (soloDropdownTimeoutRef.current) {
      clearTimeout(soloDropdownTimeoutRef.current);
    }
    setShowGroupDropdown(false);
    setShowSoloDropdown(false);
    setShowProfileDropdown(false);
    setIsOpen(false); // Also close the mobile menu
  };

  const handleClick = async () => {
    try {
      const response = await fetch("/api/songs/random");
      const data = await response.json();
      const randomId = data[0].melon_song_id;

      if (randomId) {
        window.location.pathname = `/song/${encodeId(randomId)}`;
      }
    } catch (error) {
      console.error("Error fetching the random ID:", error);
    }
  };

  const tooltip = <Tooltip id="tooltip">surprise me!</Tooltip>;

  const handleLogout = async () => {
    try {
      // Clear frontend stored data first
      setUser(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      // Dispatch custom event for login state change
      window.dispatchEvent(new Event('loginStateChange'));

      // Call backend to remove the token cookie
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include", // Include cookies in the request
      });

      if (!response.ok) {
        throw new Error(`Logout failed with status: ${response.status}`);
      }

      console.log("Logged out successfully");

      // Call Google logout last
      try {
        googleLogout();
      } catch (googleError) {
        console.log("Google logout error (non-critical):", googleError);
      }

      // Navigate to home page after logout
      window.location.pathname = "/";

    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const renderMobileNavItems = () => (
    <>
      <div className="navbar-item mobile-nav-item">
        <div
          className="mobile-nav-title"
          onClick={() => handleMobileDropdownToggle('solo')}
        >
          explore ▾
        </div>
        {showSoloDropdown && (
          <div className="mobile-subitems">
            <Link
              to="/recent-releases"
              state={{ is_idol: true, is_group: false }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              recent releases
            </Link>
            <Link
              to="/feed"
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              discover playlists
            </Link>
          </div>
        )}
      </div>

      <div className="navbar-item mobile-nav-item">
        <div
          className="mobile-nav-title"
          onClick={() => handleMobileDropdownToggle('group')}
        >
          idols ▾
        </div>
        {showGroupDropdown && (
          <div className="mobile-subitems">
            <div className="mobile-subheader">idol groups</div>
            <Link
              to="/artist-discovery/idol-groups"
              state={{ is_idol: true, is_group: true }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              all idol groups
            </Link>
            <Link
              to="/artist-discovery/idol-groups"
              state={{ is_idol: true, is_group: true, gender: "F" }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              girl groups
            </Link>
            <Link
              to="/artist-discovery/idol-groups"
              state={{ is_idol: true, is_group: true, gender: "M" }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              boy groups
            </Link>
            <Link
              to="/artist-discovery/idol-groups"
              state={{ is_idol: true, is_group: true, gender: "C" }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              co-ed groups
            </Link>

            <div className="mobile-subheader">idol solos</div>
            <Link
              to="/artist-discovery/idol-solos"
              state={{ is_idol: true, is_group: false }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              all idol solos
            </Link>
            <Link
              to="/artist-discovery/idol-solos"
              state={{ is_idol: true, is_group: false, gender: "F" }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              female idol solos
            </Link>
            <Link
              to="/artist-discovery/idol-solos"
              state={{ is_idol: true, is_group: false, gender: "M" }}
              onClick={closeDropdowns}
              className="mobile-subitem"
            >
              male idol solos
            </Link>
          </div>
        )}
      </div>

      <div className="navbar-item mobile-nav-item">
        <Link
          to="/top-credits"
          onClick={closeDropdowns}
          className="mobile-nav-title"
        >
          credits
        </Link>
      </div>

      <div className="navbar-item mobile-nav-item">
        <Link
          to="/about"
          onClick={closeDropdowns}
          className="mobile-nav-title"
        >
          about
        </Link>
      </div>

      <div className="navbar-item mobile-nav-item">
        {user ? (
          <div
            className="mobile-nav-title"
            onClick={() => {
              handleLogout();
              closeDropdowns();
            }}
          >
            log out
          </div>
        ) : (
          <Link
            to="/login"
            onClick={closeDropdowns}
            className="mobile-nav-title"
          >
            log in
          </Link>
        )}
      </div>

    </>
  );

  const renderDesktopNavItems = () => (
    <>
      <div className="navbar-item">
        <Dropdown
          onMouseEnter={handleSoloMouseEnter}
          onMouseLeave={handleSoloMouseLeave}
          show={showSoloDropdown}
          className="dropdown-wrapper"
        >
          <p>
            <Dropdown.Toggle
              variant="success"
              id="dropdown"
              className="dropdown-toggle"
              onMouseEnter={handleSoloMouseEnter}
            >
              explore
            </Dropdown.Toggle>
          </p>

          <Dropdown.Menu
            className={showSoloDropdown ? "dropdown-menu show" : "dropdown-menu hide"}
            onMouseEnter={handleSoloMouseEnter}
            onMouseLeave={handleSoloMouseLeave}
          >
            <div className="desktop-subitems">
              <Link
                to="/recent-releases"
                state={{ is_idol: true, is_group: false }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                recent releases
              </Link>
              <Link
                to="/feed"
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                discover playlists
              </Link>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="navbar-item">
        <Dropdown
          onMouseEnter={handleGroupMouseEnter}
          onMouseLeave={handleGroupMouseLeave}
          show={showGroupDropdown}
          className="dropdown-wrapper"
        >
          <p>
            <Dropdown.Toggle
              variant="success"
              id="dropdown"
              className="dropdown-toggle"
              onMouseEnter={handleGroupMouseEnter}
            >
              idols
            </Dropdown.Toggle>
          </p>

          <Dropdown.Menu
            className={showGroupDropdown ? "dropdown-menu show" : "dropdown-menu hide"}
            onMouseEnter={handleGroupMouseEnter}
            onMouseLeave={handleGroupMouseLeave}
          >
            <div className="desktop-subitems">
              <div className="desktop-subheader">idol groups</div>
              <Link
                to="/artist-discovery/idol-groups"
                state={{ is_idol: true, is_group: true }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                all idol groups
              </Link>
              <Link
                to="/artist-discovery/idol-groups"
                state={{ is_idol: true, is_group: true, gender: "F" }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                girl groups
              </Link>
              <Link
                to="/artist-discovery/idol-groups"
                state={{ is_idol: true, is_group: true, gender: "M" }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                boy groups
              </Link>
              <Link
                to="/artist-discovery/idol-groups"
                state={{ is_idol: true, is_group: true, gender: "C" }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                co-ed groups
              </Link>

              <div className="desktop-subheader">idol solos</div>
              <Link
                to="/artist-discovery/idol-solos"
                state={{ is_idol: true, is_group: false }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                all idol solos
              </Link>
              <Link
                to="/artist-discovery/idol-solos"
                state={{ is_idol: true, is_group: false, gender: "F" }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                female idol solos
              </Link>
              <Link
                to="/artist-discovery/idol-solos"
                state={{ is_idol: true, is_group: false, gender: "M" }}
                onClick={closeDropdowns}
                className="desktop-subitem"
              >
                male idol solos
              </Link>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="navbar-item">
        <Link
          to="/top-credits"
          style={{ textDecoration: "none" }}
          onClick={closeDropdowns}
        >
          <p>credits</p>
        </Link>
      </div>

      <div className="navbar-item">
        <Link
          to="/about"
          style={{ textDecoration: "none" }}
          onClick={closeDropdowns}
        >
          <p>about</p>
        </Link>
      </div>
    </>
  );

  return (
    <header>
      <div className="navbar-container" id="Navbar" ref={navbarRef}>
        <OverlayTrigger placement="bottom" overlay={tooltip}>
          <button onClick={handleClick}>
            <img className="random-icon" src="/dubuFace260.jpg" />
          </button>
        </OverlayTrigger>
        <div className="logo">
          <Link to="/" onClick={closeMenu} style={{ textDecoration: "none" }}>
            <h2 style={{ margin: 0 }}>dubu</h2>
          </Link>
        </div>
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
        ></link>
        <div className={isOpen ? "open" : "close"} id="myLinks">
          {isMobile ? renderMobileNavItems() : renderDesktopNavItems()}
        </div>

        <div className="navbar-searchbar">
          <SearchBar />
        </div>

        <div className="login-button">
          {user ? (
            <Link to={`/user/${user.dubu_user}`} style={{ textDecoration: "none" }} onClick={closeDropdowns}>
              <p>hello, {user.dubu_user}</p>
            </Link>
          ) : (
            <Link to="/login" style={{ textDecoration: "none" }} onClick={closeDropdowns}>
              <p>login</p>
            </Link>
          )}
        </div>

        {user &&
          <div className="profile-dropdown">
            <div
              className="profile-image-toggle"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            >
              <img src={getCacheBustedImageUrl(user.picture)} className="profile-image-icon"
                onError={handleImageError}
              />
            </div>

            {showProfileDropdown && (
              <div className="profile-dropdown-menu">
                <Link to={`/user/${user.dubu_user}`} onClick={closeDropdowns} className="dropdown-item">
                  profile
                </Link>
                <div
                  onClick={() => {
                    handleLogout();
                    closeDropdowns();
                  }}
                  className="dropdown-item"
                >
                  logout
                </div>
              </div>
            )}
          </div>}

        <button className="icon" onClick={toggleMenu}>
          <i className="fa fa-bars"></i>
        </button>
      </div>
    </header>
  );
};

export default Navbar;
